// Import the external URL maps to use as a base
import { SUBGRAPH_URL_BY_CHAIN_V3 } from '@kodiak-finance/smart-order-router'
import { SupportedChainId } from 'config/chains'

// --- CONSTANTS AND TYPES ---

export enum SubgraphType {
  V2 = 'v2',
  V3 = 'v3',
  BERACHAIN = 'berachain',
}

export interface SubgraphConfigEntry {
  endpoint: string
  timeout: number
}

type ChainIdConfigMap = Partial<Record<SupportedChainId, SubgraphConfigEntry>>
type SubgraphUrlMap = Record<SupportedChainId, string>

const DEFAULT_TIMEOUT_MS = 30_000

// --- 1. OVERRIDES AND BASE CONFIGURATION ---

// Define OVERRIDES for V2/V3 based on external maps
const V2_URL_OVERRIDES: Partial<SubgraphUrlMap> = {
  // Add other V2 overrides here
  [SupportedChainId.BERA]:
    'https://api.subgraph.ormilabs.com/api/public/d7eed6cc-ad4a-4862-8017-89893c4095d3/subgraphs/kodiak-v3/latest/gn',
}

const V3_URL_OVERRIDES: Partial<SubgraphUrlMap> = {
  // Add other V3 overrides here
  [SupportedChainId.BERA]:
    'https://api.subgraph.ormilabs.com/api/public/d7eed6cc-ad4a-4862-8017-89893c4095d3/subgraphs/kodiak-v3/latest/gn',
}

// Define BERACHAIN config directly
const BERACHAIN_SUBGRAPH_CONFIG: Partial<SubgraphUrlMap> = {
  [SupportedChainId.BERA]: 'https://api.berachain.com',
}

// --- 2. FINAL URL MAPS FOR EXTERNAL USE ---

/**
 * Final V2 Subgraph URLs, merged with external defaults and overrides.
 */
export const FINAL_SUBGRAPH_URLS_V2: SubgraphUrlMap = {
  ...SUBGRAPH_URL_BY_CHAIN_V3, //we use v3 subgraph in smart-order-router also
  ...V2_URL_OVERRIDES,
} as SubgraphUrlMap

/**
 * Final V3 Subgraph URLs, merged with external defaults and overrides.
 */
export const FINAL_SUBGRAPH_URLS_V3: SubgraphUrlMap = {
  ...SUBGRAPH_URL_BY_CHAIN_V3,
  ...V3_URL_OVERRIDES,
} as SubgraphUrlMap

// --- 3. INTERNAL CONFIGURATION MAP FOR `getSubgraphConfig` ---

const createChainIdConfigMap = (urls: SubgraphUrlMap): ChainIdConfigMap => {
  return Object.entries(urls).reduce((acc, [chainId, endpoint]) => {
    acc[Number(chainId) as SupportedChainId] = { endpoint, timeout: DEFAULT_TIMEOUT_MS }
    return acc
  }, {} as ChainIdConfigMap)
}

export const SUBGRAPH_ENDPOINTS: Record<SubgraphType, ChainIdConfigMap> = {
  [SubgraphType.V3]: createChainIdConfigMap(FINAL_SUBGRAPH_URLS_V3),
  [SubgraphType.V2]: createChainIdConfigMap(FINAL_SUBGRAPH_URLS_V2),
  [SubgraphType.BERACHAIN]: createChainIdConfigMap(BERACHAIN_SUBGRAPH_CONFIG as SubgraphUrlMap),
} as const

// --- 4. EXPORTED HELPER FUNCTIONS ---

/**
 * Retrieves the configuration entry (endpoint, timeout) for a given subgraph type and chain ID.
 */
export function getSubgraphConfig(chainId: number, subgraphType: SubgraphType): SubgraphConfigEntry {
  const endpoints = SUBGRAPH_ENDPOINTS[subgraphType]
  const config = endpoints[chainId as SupportedChainId]

  if (!config) {
    throw new Error(`No ${subgraphType} endpoint for chain ${chainId}`)
  }

  return config
}
